import React from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, FileText, GraduationCap, Headphones } from 'lucide-react';
import { cn } from 'lib/utils';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import type { IeltsScope, IeltsScopeSkill, IeltsVariant } from '@examing/types';
import { IELTS_PART_COUNT, IELTS_SCOPE_SKILLS, SCOPE_PART_KEY, SCOPE_SKILL_KEY, scopeMode, type IeltsScopeMode } from '../../constants';

export type TestFormat = 'STANDARD' | 'IELTS' | 'SAT';

interface Props {
  format: TestFormat;
  onFormat: (format: TestFormat) => void;
  ieltsVariant: IeltsVariant;
  onIeltsVariant: (variant: IeltsVariant) => void;
  /** What an IELTS test covers; undefined is the full test. */
  ieltsScope: IeltsScope | undefined;
  onIeltsScope: (scope: IeltsScope | undefined) => void;
}

const SCOPE_MODES: IeltsScopeMode[] = ['FULL', 'SKILL', 'PART'];

function Pill({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        pressed ? 'border-foreground bg-foreground text-background' : 'border-border',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Format step of test creation. A standard test is today's flow; IELTS-style
 * and Digital SAT practice tests get their sections created for them. The
 * format cannot be changed after the test is created.
 */
export function FormatPicker({ format, onFormat, ieltsVariant, onIeltsVariant, ieltsScope, onIeltsScope }: Props) {
  const { t } = useTranslation();
  const hasIelts = useFeature(FEATURES.IELTS_SUITE);
  const hasSat = useFeature(FEATURES.SAT_SUITE);
  const upgrade = useOpenUpgradeForFeature();
  const mode = scopeMode(ieltsScope);

  const chooseMode = (next: IeltsScopeMode) => {
    const skill: IeltsScopeSkill = ieltsScope?.skill ?? 'LISTENING';
    if (next === 'FULL') onIeltsScope(undefined);
    else if (next === 'SKILL') onIeltsScope({ skill });
    else onIeltsScope({ skill, part: ieltsScope?.part ?? 1 });
  };
  const chooseSkill = (skill: IeltsScopeSkill) => onIeltsScope(mode === 'PART' ? { skill, part: 1 } : { skill });

  const options: Array<{ key: TestFormat; icon: typeof FileText; title: string; body: string; locked: boolean; feature?: string }> = [
    { key: 'STANDARD', icon: FileText, title: t('exam.format.standard'), body: t('exam.format.standard_body'), locked: false },
    { key: 'IELTS', icon: Headphones, title: t('exam.format.ielts'), body: t('exam.format.ielts_body'), locked: !hasIelts, feature: FEATURES.IELTS_SUITE },
    { key: 'SAT', icon: GraduationCap, title: t('exam.format.sat'), body: t('exam.format.sat_body'), locked: !hasSat, feature: FEATURES.SAT_SUITE },
  ];

  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-sm font-medium">{t('exam.format.title')}</legend>
      <div role="radiogroup" className="grid grid-cols-1 gap-2">
        {options.map((o) => {
          const selected = format === o.key;
          return (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => (o.locked && o.feature ? upgrade(o.feature) : onFormat(o.key))}
              className={cn(
                'flex items-start gap-3 rounded-xl border p-3.5 text-left transition-[background-color,border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected ? 'border-foreground bg-muted/60 shadow-xs' : 'border-border hover:border-foreground/30 hover:bg-muted/40',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                  selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/80',
                )}
              >
                <o.icon className="h-[18px] w-[18px]" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-foreground">
                  {o.title}
                  {o.locked && (
                    <span
                      className="inline-flex items-center gap-1 rounded-md bg-warning-50 px-1.5 py-0.5 text-[11px] font-semibold text-warning-700"
                      aria-label={t('exam.format.upgrade')}
                    >
                      <Crown className="h-3 w-3" aria-hidden />
                      PRO
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{o.body}</span>
              </span>
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                  selected ? 'border-foreground' : 'border-border',
                )}
              >
                {selected && <span className="h-2.5 w-2.5 rounded-full bg-foreground" />}
              </span>
            </button>
          );
        })}
      </div>
      {format === 'IELTS' && (
        <div className="space-y-4 pt-1">
          <div className="flex gap-2">
            {(['ACADEMIC', 'GENERAL'] as const).map((v) => (
              <Pill key={v} pressed={ieltsVariant === v} onClick={() => onIeltsVariant(v)}>
                {v === 'ACADEMIC' ? t('exam.ielts.academic') : t('exam.ielts.general')}
              </Pill>
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">{t('exam.scope.title')}</p>
            <div role="group" aria-label={t('exam.scope.title')} className="flex flex-wrap gap-2">
              {SCOPE_MODES.map((m) => (
                <Pill key={m} pressed={mode === m} onClick={() => chooseMode(m)}>
                  {t(`exam.scope.mode_${m.toLowerCase()}`)}
                </Pill>
              ))}
            </div>
            {ieltsScope && (
              <div role="group" aria-label={t('exam.scope.skill')} className="flex flex-wrap gap-2">
                {IELTS_SCOPE_SKILLS.map((skill) => (
                  <Pill key={skill} pressed={ieltsScope.skill === skill} onClick={() => chooseSkill(skill)}>
                    {t(SCOPE_SKILL_KEY[skill])}
                  </Pill>
                ))}
              </div>
            )}
            {ieltsScope && mode === 'PART' && (
              <div role="group" aria-label={t('exam.scope.part')} className="flex flex-wrap gap-2">
                {Array.from({ length: IELTS_PART_COUNT[ieltsScope.skill] }, (_, i) => i + 1).map((n) => (
                  <Pill key={n} pressed={ieltsScope.part === n} onClick={() => onIeltsScope({ skill: ieltsScope.skill, part: n })}>
                    {t(SCOPE_PART_KEY[ieltsScope.skill], { n })}
                  </Pill>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">{t(`exam.scope.help_${mode.toLowerCase()}`)}</p>
          </div>
        </div>
      )}
      {format !== 'STANDARD' && <p className="text-xs text-muted-foreground">{t('exam.format.fixed_note')}</p>}
    </fieldset>
  );
}

export default FormatPicker;
